/**
 * Katalog gezinme servisi — "Katalogdan Ekle" ekranının veri kaynağı.
 *
 * Katalog TÜM şirketlerin ortak referans listesidir ve ana DB'de durur, o yüzden
 * çağrılar muhasebe API'sine değil netapi'ye (`mainApi`) gider. Süper admin yönetim
 * ucundan (`/api/superadmin/catalog`) ayrı, salt okunur bir yüzeydir
 * (`/api/catalog`): oturum açmış her kiracı kullanıcısı okuyabilir, kimse yazamaz.
 */

import { mainApi } from '../../../../config/apiClient';

/** Sektör butonları — yalnızca aktif ürünü olan sektörler döner. */
export interface CatalogBrowseSector {
  id: number;
  name: string;
  productCount: number;
}

/**
 * Kategori düz listesi; 2 seviyeli ağacı `parentId` ile frontend kurar.
 * `productCount` kategorinin DOĞRUDAN ürün adedidir — ürünler en derin seviyeye bağlı
 * olduğu için üst kategorilerde genelde 0'dır, toplamı `rollUpCounts` hesaplar.
 */
export interface CatalogBrowseCategory {
  id: number;
  sectorId: number;
  parentId: number | null;
  name: string;
  productCount: number;
}

export interface CatalogBrowseProduct {
  id: number;
  name: string;
  barcode: string | null;
  catalogCode: string | null;
  sectorId: number;
  sectorName: string | null;
  categoryId: number | null;
  categoryName: string | null;
  categoryPath: string | null;
  vatRate: number;
  vatStatus: string;
  unit: string;
  departmentNo: number | null;
  imageUrl: string | null;
}

export interface CatalogBrowsePage {
  items: CatalogBrowseProduct[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CatalogBrowseQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  /**
   * Aramayı tek alana daraltır. Boş bırakılırsa ad + barkod + katalog kodu birlikte
   * taranır. `barcode` modunda backend baştan eşleşme arar.
   */
  searchField?: 'name' | 'barcode';
  sectorId?: number;
  categoryId?: number;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

/** Backend'in tek istekte döndürebileceği üst sınır; toplu aktarım bu boyutta tarar. */
export const CATALOG_BROWSE_MAX_PAGE_SIZE = 500;

/** İki seviyeli kategori ağacının bir düğümü. */
export interface CatalogCategoryNode extends CatalogBrowseCategory {
  children: CatalogCategoryNode[];
  /** Kendi ürünleri + tüm alt kategorilerinin ürünleri. */
  totalProductCount: number;
  /** Alt kategorisi olmayan düğüm; ürün listesi yalnızca yaprakta açılır. */
  isLeaf: boolean;
}

export class CatalogBrowseService {
  static async getSectors(): Promise<CatalogBrowseSector[]> {
    const response = await mainApi.get('/api/catalog/sectors');
    return response.data?.data ?? [];
  }

  static async getCategories(sectorId: number): Promise<CatalogBrowseCategory[]> {
    const response = await mainApi.get('/api/catalog/categories', { params: { sectorId } });
    return response.data?.data ?? [];
  }

  static async getProductsPaged(query: CatalogBrowseQuery): Promise<CatalogBrowsePage> {
    const response = await mainApi.get('/api/catalog/products/paged', {
      params: {
        page: query.page ?? 1,
        pageSize: query.pageSize ?? 50,
        search: query.search || undefined,
        searchField: query.searchField || undefined,
        sectorId: query.sectorId || undefined,
        categoryId: query.categoryId || undefined,
        sortColumn: query.sortColumn || undefined,
        sortDirection: query.sortDirection || undefined,
      },
    });

    const body = response.data ?? {};
    return {
      items: body.data ?? [],
      totalCount: body.totalCount ?? 0,
      page: body.page ?? 1,
      pageSize: body.pageSize ?? (query.pageSize ?? 50),
      totalPages: body.totalPages ?? 0,
    };
  }

  /**
   * Bir kategorinin (üst kategori seçilirse alt kategorileri dahil) TÜM ürünlerini
   * sayfa sayfa toplar. Toplu aktarımda kullanılır.
   *
   * `onProgress` her sayfadan sonra çağrılır; `limit` aşılırsa tarama durur —
   * kullanıcı kazara devasa bir seçim yaparsa tarayıcıyı kilitlememek için.
   */
  static async collectProducts(
    query: { sectorId?: number; categoryId?: number; search?: string },
    limit: number,
    onProgress?: (collected: number, total: number) => void
  ): Promise<CatalogBrowseProduct[]> {
    const collected: CatalogBrowseProduct[] = [];
    let page = 1;

    do {
      const result = await CatalogBrowseService.getProductsPaged({
        ...query,
        page,
        pageSize: CATALOG_BROWSE_MAX_PAGE_SIZE,
      });

      collected.push(...result.items);
      onProgress?.(collected.length, Math.min(result.totalCount, limit));

      if (result.items.length === 0 || page >= result.totalPages) break;
      page += 1;
    } while (collected.length < limit);

    return collected.slice(0, limit);
  }
}

/**
 * Düz kategori listesinden 2 seviyeli ağaç kurar ve üst kategorilerin toplam ürün
 * sayısını alt kategorilerden toplayarak hesaplar.
 *
 * Alt kategorisi olmayan bir üst kategori (katalogda "Sınıflandırılamadı" böyle) yaprak
 * sayılır; aksi hâlde içindeki ürünlere gezinerek ulaşmanın yolu kalmazdı.
 */
export function buildCategoryTree(categories: CatalogBrowseCategory[]): CatalogCategoryNode[] {
  const nodes = new Map<number, CatalogCategoryNode>();
  categories.forEach((c) => {
    nodes.set(c.id, { ...c, children: [], totalProductCount: c.productCount, isLeaf: true });
  });

  const roots: CatalogCategoryNode[] = [];
  nodes.forEach((node) => {
    const parent = node.parentId != null ? nodes.get(node.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
      parent.isLeaf = false;
      parent.totalProductCount += node.totalProductCount;
    } else {
      roots.push(node);
    }
  });

  // Ürünü olmayan dalları gizle: kiracı boş kategoriye tıklayıp boş tablo görmesin.
  const prune = (list: CatalogCategoryNode[]): CatalogCategoryNode[] =>
    list
      .filter((n) => n.totalProductCount > 0)
      .map((n) => ({ ...n, children: prune(n.children) }))
      .sort((a, b) => a.name.localeCompare(b.name, 'tr'));

  return prune(roots);
}
